import {
  Box,
  FlagAvatar,
  FormField,
  Select,
  TextInput,
} from '@percent/lemonade'
import { type Address, AddressLookup } from '@percent/domain/giving'
import { useEffect, useMemo, useState } from 'react'
import type { Alpha2Code } from 'i18n-iso-countries'

import type { OpportunityFormLocationSectionProps } from './opportunity-form-location-section.types'
import styles from './offline-location-fields.module.scss'

export function OfflineLocationFields({
  value,
  preFillAddressLookup,
  onChange,
  handleFormChange,
  onBlur,
  touched,
  setTouched,
  validate,
  errors,
  forceReadOnly,

  allowedCountryCodes,
  countryHelpers,
  googleMapsApiKey,
  language,

  lookupLocationLabel,
  streetAddressLabel,
  streetAddressPlaceholder,
  apartmentLabel,
  apartmentPlaceholder,
  cityLabel,
  cityPlaceholder,
  countryLabel,
  countryPlaceholder,
  zipOrPostalCodeLabel,
  zipOrPostalCodePlaceholder,
}: Readonly<OpportunityFormLocationSectionProps>) {
  const countryPickerOptions = useMemo(
    () =>
      allowedCountryCodes.map((a) => ({
        value: a,
        label: countryHelpers.getName(a, 'en'),
        prefix: (
          <FlagAvatar code={countryHelpers.alpha3ToAlpha2(a) as Alpha2Code} />
        ),
      })),
    [allowedCountryCodes, countryHelpers]
  )

  const defaultCountryValue = countryPickerOptions.filter(
    (a) => a.value === value.country
  )[0]

  const [addressState, setAddressState] = useState<Address>({
    addressLineOne: undefined,
    addressLineTwo: undefined,
    city: undefined,
    zipCode: undefined,
    country: undefined,
  })

  useEffect(() => {
    if (!preFillAddressLookup) return

    setAddressState({
      addressLineOne: value.addressLineOne,
      addressLineTwo: value.addressLineTwo,
      city: value.city,
      zipCode: value.zipCode,
      country: value.country,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preFillAddressLookup])

  const addressFound =
    forceReadOnly ||
    Object.values(addressState).filter((a) => a !== undefined).length > 0

  return (
    <>
      {!forceReadOnly && (
        <FormField label={lookupLocationLabel} necessity="required">
          <AddressLookup
            onSelect={(address) => {
              const requiredFields = [
                'addressLineOne',
                'city',
                'country',
                'zipCode',
              ] as const
              setAddressState(address)
              onChange('addressLineOne', address.addressLineOne ?? '')
              onChange('addressLineTwo', address.addressLineTwo ?? '')
              onChange('city', address.city ?? '')

              if (address.country) {
                const countryMatch = countryPickerOptions.find(
                  (a) => a.label === address.country
                )

                if (countryMatch) {
                  onChange('country', countryMatch.value)
                  onChange('targetCountries', [countryMatch.value])
                }
              }

              onChange('zipCode', address.zipCode ?? '')

              if (address.lat) {
                onChange('lat', address.lat)
              }

              if (address.long) {
                onChange('long', address.long)
              }

              setTimeout(async () => {
                requiredFields.forEach((field) => setTouched(field))
              }, 1)
              setTimeout(
                () =>
                  validate(['addressLineOne', 'city', 'country', 'zipCode']),
                1
              )
            }}
            googleMapsApiKey={googleMapsApiKey}
            language={language}
          />
        </FormField>
      )}

      {addressFound && (
        <>
          <Box className={styles.AddressLinesWrapper}>
            <FormField
              label={streetAddressLabel}
              necessity="required"
              status={
                touched.addressLineOne && errors.addressLineOne
                  ? 'danger'
                  : 'default'
              }
              statusMessage={
                typeof errors.addressLineOne === 'string'
                  ? errors.addressLineOne
                  : errors.addressLineOne?.join(', ')
              }
              data-testid="addressLineOneField"
              disabled={forceReadOnly}
            >
              <TextInput
                name="addressLineOne"
                value={value.addressLineOne}
                placeholder={streetAddressPlaceholder}
                onBlur={onBlur}
                onChange={handleFormChange}
                borderRadiiSize="large"
                disabled={forceReadOnly}
              />
            </FormField>
            <FormField
              label={apartmentLabel}
              necessity="optional"
              status={
                touched.addressLineTwo && errors.addressLineTwo
                  ? 'danger'
                  : 'default'
              }
              statusMessage={
                typeof errors.addressLineTwo === 'string'
                  ? errors.addressLineTwo
                  : errors.addressLineTwo?.join(', ')
              }
              data-testid="addressLineTwoField"
              disabled={forceReadOnly}
            >
              <TextInput
                name="addressLineTwo"
                value={value.addressLineTwo}
                placeholder={apartmentPlaceholder}
                onBlur={onBlur}
                onChange={handleFormChange}
                borderRadiiSize="large"
                disabled={forceReadOnly}
              />
            </FormField>
          </Box>
          <Box className={styles.AddressLinesWrapper}>
            <FormField
              label={cityLabel}
              necessity="required"
              status={touched.city && errors.city ? 'danger' : 'default'}
              statusMessage={
                typeof errors.city === 'string'
                  ? errors.city
                  : errors.city?.join(', ')
              }
              data-testid="cityField"
              disabled={forceReadOnly || !!addressState.city}
            >
              <TextInput
                name="city"
                value={value.city}
                placeholder={cityPlaceholder}
                onBlur={onBlur}
                onChange={handleFormChange}
                borderRadiiSize="large"
                disabled={forceReadOnly}
              />
            </FormField>
            <FormField
              label={countryLabel}
              necessity="required"
              status={touched.country && errors.country ? 'danger' : 'default'}
              statusMessage={
                typeof errors.country === 'string'
                  ? errors.country
                  : errors.country?.join(', ')
              }
              data-testid="countryField"
              disabled={forceReadOnly || !!addressState.country}
            >
              <Select
                placeholder={countryPlaceholder}
                status={errors.country ? 'danger' : 'default'}
                options={countryPickerOptions}
                defaultValue={defaultCountryValue}
                onChange={(event) => {
                  onChange('country', event.value)
                }}
                searchable
                borderRadiiSize="large"
                disabled={forceReadOnly}
              />
            </FormField>
            <FormField
              label={zipOrPostalCodeLabel}
              necessity="required"
              status={touched.zipCode && errors.zipCode ? 'danger' : 'default'}
              statusMessage={
                typeof errors.zipCode === 'string'
                  ? errors.zipCode
                  : errors.zipCode?.join(', ')
              }
              data-testid="zipCodeField"
              disabled={forceReadOnly || !!addressState.zipCode}
            >
              <TextInput
                name="zipCode"
                value={value.zipCode}
                placeholder={zipOrPostalCodePlaceholder}
                onBlur={onBlur}
                onChange={handleFormChange}
                borderRadiiSize="large"
                disabled={forceReadOnly}
              />
            </FormField>
          </Box>
        </>
      )}
    </>
  )
}
